
import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './HeroComponent.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { useWidthContext } from '../../providers/Width';
import withAsyncImport from '../../withAsyncImport';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import HeroOverlap from './HeroOverlap/HeroOverlap';
import { Image, TextLine } from '../../importCL';

const CenterHero = withAsyncImport(() => import('./CenterHero/CenterHero'));

interface IPQIcon {
    iconPath: string;
    iconAltText: string;
}
export interface IHeroComponentProps extends MappedComponentProperties {

    className?: string;
    allowPrint?: boolean;
    bgColor?: 'light-grey' | 'blue-denim' | 'blue-creditone' | 'purple' | 'white' | 'gradient';
    backgroundColor?: 'white' | 'blue-creditone' | 'light-blue' | 'purple' | 'grey-alabaster';
    variantSelect?: 'articlesVariant' | 'partnershipsVariant' | 'pullQuoteVariant' | 'browseCardVariant' | "aboutUs";
    justifyText?: 'left' | 'center' | 'right';
    overlayPosition?: 'right' | 'left';
    ctaType?: 'ghosted-light' | 'ghosted-dark' | 'link' | 'chip' | 'solid';
    ctaSize?: 'small' | 'medium' | 'large';
    ctaIcon?: 'lock' | 'chevron' | 'back' | '';
    articlesMarginControl?: string;
    bgOverlay?: boolean;
    bgImgPath?: string;
    altText?: string;
    mobileBgImgPath?: string;
    mobileAltText?: string;
    pageTitle?: string;
    pageDesc?: string;
    mobilePageTitle?: string;
    mobilePageDesc?: string;
    overlayUpload?: string;
    overlayMarginTop?: string;
    overlayMarginBottom?: string;
    heroCenterSwitch?: boolean;
    lightTextOption?: boolean;
    gradientLineRemoved?: boolean;
    label?: string;
    link?: string;
    newTab?: boolean;
    pullQuoteText?: string;
    pullQuoteIconLabel?: IPQIcon;
    pullQuoteName?: string;
    pullQuoteTitle?: string;
    preloadDesktopImage?: boolean;
    disclaimer?: string;
}

export const HeroComponentEditConfig = {
    emptyLabel: 'HeroComponent',
    isEmpty: (props: PropsWithChildren<IHeroComponentProps>) => {
        return !props || !props.bgImgPath;
    },
};


const HeroComponent = (props: PropsWithChildren<IHeroComponentProps>): JSX.Element => {
    const { pageDesc = '' } = props;
    const { pageTitle = '' } = props;
    const { ctaSize = 'small' } = props;
    const { ctaIcon = '' } = props;
    const buttonTarget = props.newTab ? '_blank' : '_self';
    const { width } = useWidthContext();
    const isMobile = width <= 768;
    const bgImage = isMobile && props.mobileBgImgPath ? props.mobileBgImgPath : props.bgImgPath;
    const alText = isMobile && props.mobileAltText ? props.mobileAltText : props.altText;
    const isBgDark = props.bgColor === 'blue-creditone' || props.bgColor === 'purple';

    if (props.variantSelect === 'aboutUs') {
        return (
        <div id='featured-article' className={`bg-${props.backgroundColor} flex md:flex-col md:p-0 justify-center px-12`}>
                <div className='flex max-w-[28rem] pt-8 pb-4 pr-8 md:max-w-full md:pl-4'>
                    <TextLine 
                        tag='h2' 
                        variant="author" 
                        authorClass='!text-[1.688rem] !leading-[2.188rem]'
                        subHeading={props.pageDesc ? transformHTMLParserLinks(wrapTrademarkSuperscript(props.pageDesc)) : ''}
                        isDarkMode={props.backgroundColor === 'purple' || props.backgroundColor === 'blue-creditone'}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.pageTitle))}
                    </TextLine>
                </div>
                <div className='flex justify-center items-center'>
                    <Image
                        src={props.bgImgPath}
                        alt={props.altText}
                        fetchpriority='high'
                        rel='preload'
                        className='max-h-[25rem] lg:m-0 md:justify-center'
                    />
                </div>
            </div>
        )
    }
    const preLoadDesktopImage = () => {
        return (
            props.preloadDesktopImage && props.bgImgPath &&
            <img className='hidden' loading='lazy' src={props.bgImgPath} />
        );
    };

    if (props.variantSelect === 'browseCardVariant') {
        return (
            <> {preLoadDesktopImage()}
                <CenterHero role='img' {...props} width={width} />
            </>
        );
    }

    return (
        <div id='hero-component' className={`HeroComponent ${props.className}`}>
            {preLoadDesktopImage()}
            {
                !props.heroCenterSwitch &&
                (<HeroOverlap
                    className={props.className}
                    title={transformHTMLParserLinks(pageTitle)}
                    bgimg={bgImage}
                    bgColor={props.bgColor || 'white'}
                    ariaLabel={alText}
                    removetitleHr={props.gradientLineRemoved || isBgDark}>
                    {pageDesc && (<span>{transformHTMLParserLinks(pageDesc)}</span>)}
                    {props.label && props.link &&
                        <ButtonWrapper
                            className='HeroComponent__Button'
                            href={props.link}
                            variant={props.ctaType}
                            icon={ctaIcon === '' ? undefined : ctaIcon}
                            size={ctaSize}
                            target={buttonTarget}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.label))}
                        </ButtonWrapper>
                    }
                </HeroOverlap>)
            }
            {
                props.heroCenterSwitch &&
                (<CenterHero {...props} width={width}
                />)
            }
        </div>
    );
};

export default HeroComponent;
